/**
 * useVideoDelay Hook
 * Buffers video frames and plays them back with a configurable delay
 * Used for lip-sync with translated audio
 */

import { useRef, useEffect, useCallback, useState } from 'react';

// Default delay for translation pipeline: STT(500ms) + LLM(300ms) + TTS(400ms) ≈ 1.2s
const DEFAULT_DELAY_MS = 1500;
const FRAME_RATE = 30; // 30 fps capture

export function useVideoDelay(enabled = false, delayMs = DEFAULT_DELAY_MS) {
  const sourceVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameBufferRef = useRef([]);
  const animationRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [bufferFillPercent, setBufferFillPercent] = useState(0);
  const [debugStatus, setDebugStatus] = useState('idle');

  // Calculate buffer size based on delay and frame rate
  const bufferSize = Math.ceil((delayMs / 1000) * FRAME_RATE);

  // Set source video element
  const setSourceVideo = useCallback((videoElement) => {
    console.log('[VideoDelay] setSourceVideo called:', videoElement ? 'element set' : 'null');
    sourceVideoRef.current = videoElement;

    if (videoElement) {
      setDebugStatus('video-set');

      // Ensure video plays when set
      if (videoElement.paused) {
        videoElement.play().catch(e => {
          console.log('[VideoDelay] Video play error:', e);
          setDebugStatus('play-error');
        });
      }

      // Listen for video ready
      const onCanPlay = () => {
        console.log('[VideoDelay] Video can play');
        setDebugStatus('video-ready');
      };
      videoElement.addEventListener('canplay', onCanPlay, { once: true });
    }
  }, []);

  // Start/stop capturing based on enabled state
  useEffect(() => {
    if (!enabled) {
      // Stop and cleanup
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      frameBufferRef.current = [];
      setIsBuffering(false);
      setBufferFillPercent(0);
      console.log('[VideoDelay] Stopped');
      return;
    }

    // Clear buffer when enabling
    frameBufferRef.current = [];
    setIsBuffering(false);
    setBufferFillPercent(0);

    console.log(`[VideoDelay] Starting with ${delayMs}ms delay, buffer size: ${bufferSize} frames`);

    // Capture frame function
    const captureFrame = () => {
      const video = sourceVideoRef.current;

      if (!video) {
        setDebugStatus('no-video');
        return;
      }

      // Check if video is ready (readyState >= 2 means HAVE_CURRENT_DATA)
      if (video.readyState < 2) {
        setDebugStatus(`wait-rs${video.readyState}`);
        return;
      }

      if (!video.videoWidth || !video.videoHeight) {
        setDebugStatus('no-dims');
        return;
      }

      setDebugStatus('capturing');

      try {
        // Create offscreen canvas for frame capture
        const frameCanvas = document.createElement('canvas');
        frameCanvas.width = video.videoWidth;
        frameCanvas.height = video.videoHeight;
        const ctx = frameCanvas.getContext('2d');

        // Draw current video frame
        ctx.drawImage(video, 0, 0);

        // Store frame with timestamp
        const frame = {
          imageData: ctx.getImageData(0, 0, frameCanvas.width, frameCanvas.height),
          width: frameCanvas.width,
          height: frameCanvas.height,
          timestamp: Date.now(),
        };

        frameBufferRef.current.push(frame);

        // Remove old frames beyond buffer size (keep 1.5x for smooth playback)
        const maxBufferSize = Math.ceil(bufferSize * 1.5);
        while (frameBufferRef.current.length > maxBufferSize) {
          frameBufferRef.current.shift();
        }

        // Update buffer fill percentage
        const percent = Math.min(100, Math.round((frameBufferRef.current.length / bufferSize) * 100));
        setBufferFillPercent(percent);

        // Mark as ready when we have enough frames
        if (frameBufferRef.current.length >= bufferSize) {
          setIsBuffering(true);
          setDebugStatus('ready');
        }
      } catch (e) {
        console.error('[VideoDelay] Frame capture error:', e);
      }
    };

    // Draw delayed frame function
    const drawDelayedFrame = () => {
      const canvas = canvasRef.current;

      if (!canvas) {
        animationRef.current = requestAnimationFrame(drawDelayedFrame);
        return;
      }

      if (frameBufferRef.current.length === 0) {
        animationRef.current = requestAnimationFrame(drawDelayedFrame);
        return;
      }

      const now = Date.now();
      const targetTime = now - delayMs;

      // Find the frame closest to the target time
      let frameToShow = null;
      let frameIndex = -1;

      for (let i = 0; i < frameBufferRef.current.length; i++) {
        const frame = frameBufferRef.current[i];
        if (frame.timestamp <= targetTime) {
          frameToShow = frame;
          frameIndex = i;
        } else {
          break;
        }
      }

      // Remove old frames that have been shown
      if (frameIndex > 0) {
        frameBufferRef.current.splice(0, frameIndex);
      }

      // Draw the frame
      if (frameToShow) {
        const ctx = canvas.getContext('2d');

        // Resize canvas if needed
        if (canvas.width !== frameToShow.width || canvas.height !== frameToShow.height) {
          canvas.width = frameToShow.width;
          canvas.height = frameToShow.height;
        }

        ctx.putImageData(frameToShow.imageData, 0, 0);
      }

      animationRef.current = requestAnimationFrame(drawDelayedFrame);
    };

    // Start frame capture at fixed interval
    const captureMs = 1000 / FRAME_RATE;
    captureIntervalRef.current = setInterval(captureFrame, captureMs);

    // Start rendering loop
    animationRef.current = requestAnimationFrame(drawDelayedFrame);

    // Cleanup
    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [enabled, delayMs, bufferSize]);

  return {
    setSourceVideo,
    canvasRef,
    isBuffering,
    bufferFillPercent,
    debugStatus,
  };
}

export default useVideoDelay;
